import React, { useEffect, useState, useContext } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthProvider";
import { getProvisionStatus } from "../../lib/api/v2Api";
import { device } from "../../lib/breakpoints";

/**
 * V2 Triage Dashboard.
 * Phase 2 stub: shows the live phone line info and an empty-state triage feed.
 * Phase 3 will replace the feed with real call/text history from Firestore.
 */
export default function V2Dashboard() {
  const { user, userInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const [phone, setPhone] = useState(null);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getProvisionStatus(user)
      .then((status) => {
        if (status.provisioned) {
          setPhone(status.formattedPhone || status.phoneNumber);
          setAddress(status.propertyAddress || "");
        }
      })
      .catch((err) => console.error("[V2Dashboard] status error:", err))
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <Wrap>
      <TopBar>
        <TopBarLeft>
          <DashTitle>Triage Dashboard</DashTitle>
          <DashSub>AI Front Desk</DashSub>
        </TopBarLeft>
        <TopBarRight>
          <SettingsBtn onClick={() => navigate("/profile")}>
            Emergency Routing Settings
          </SettingsBtn>
        </TopBarRight>
      </TopBar>

      {!loading && phone && (
        <LineCard>
          <LineLabel>Your AI line is live</LineLabel>
          <LinePhone>{phone}</LinePhone>
          {address && <LineAddress>{address}</LineAddress>}
          <LineHint>
            Inbound messages hit Clumse triage first. Clumse AI summarizes the
            thread. Your dashboard keeps the transcript. Only true emergencies reach
            you per your routing rules.
          </LineHint>
        </LineCard>
      )}

      <FeedSection>
        <FeedHeader>
          <FeedTitle>Your Triage Feed</FeedTitle>
          <FeedBadge>Live</FeedBadge>
        </FeedHeader>

        <EmptyState>
          <EmptyIcon aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </EmptyIcon>
          <EmptyTitle>No threads yet.</EmptyTitle>
          <EmptyBody>
            Your triage feed appears here after occupant-style activity. Open chat
            settings and run a test thread first.
            {phone ? (
              <>
                {" "}
                Your provisioned line is <PhoneEmphasis>{phone}</PhoneEmphasis>{" "}
                when you want to point real contacts at it.
              </>
            ) : null}
          </EmptyBody>
          {phone && (
            <CallNowBtn onClick={() => navigate("/v2/simulation")}>
              Back to setup summary
            </CallNowBtn>
          )}
        </EmptyState>
      </FeedSection>

      <HintFooter>
        Phase 3 will populate this feed with triage transcripts, labels, and
        one-tap escalation controls.
      </HintFooter>
    </Wrap>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const dotPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

const Wrap = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #0a0f0a 0%, #0d1f0e 100%);
  padding: 0 0 4rem;
  animation: ${fadeIn} 0.4s ease;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(74, 222, 128, 0.1);
  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
    padding: 1.25rem 1.25rem;
  }
`;

const TopBarLeft = styled.div``;

const DashTitle = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  color: #f0fdf4;
  margin: 0;
  font-family: var(--font-primary, sans-serif);
`;

const DashSub = styled.p`
  font-size: 0.75rem;
  color: #4ade80;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0.15rem 0 0;
`;

const TopBarRight = styled.div``;

const SettingsBtn = styled.button`
  padding: 0.55rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  background: transparent;
  color: rgba(240, 253, 244, 0.6);
  border: 1px solid rgba(240, 253, 244, 0.15);
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--font-secondary, sans-serif);
  transition: border-color 0.2s, color 0.2s;
  &:hover {
    border-color: rgba(74, 222, 128, 0.4);
    color: #f0fdf4;
  }
`;

const LineCard = styled.div`
  margin: 2rem 2rem 0;
  background: rgba(74, 222, 128, 0.05);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 16px;
  padding: 1.5rem 1.75rem;
  @media ${device.mobile} {
    margin: 1.25rem 1.25rem 0;
  }
`;

const LineLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #4ade80;
  margin: 0 0 0.4rem;
`;

const LinePhone = styled.div`
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  font-weight: 800;
  color: #f0fdf4;
  letter-spacing: 0.04em;
  font-family: var(--font-primary, monospace);
  margin-bottom: 0.25rem;
`;

const LineAddress = styled.p`
  font-size: 0.9rem;
  color: rgba(240, 253, 244, 0.55);
  margin: 0 0 0.75rem;
  font-family: var(--font-secondary, sans-serif);
`;

const LineHint = styled.p`
  font-size: 0.875rem;
  color: rgba(240, 253, 244, 0.5);
  margin: 0;
  line-height: 1.55;
  font-family: var(--font-secondary, sans-serif);
`;

const FeedSection = styled.div`
  margin: 2rem 2rem 0;
  @media ${device.mobile} {
    margin: 1.5rem 1.25rem 0;
  }
`;

const FeedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const FeedTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 600;
  color: #f0fdf4;
  margin: 0;
  font-family: var(--font-primary, sans-serif);
`;

const FeedBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #4ade80;
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.25);
  border-radius: 100px;
  padding: 0.2rem 0.6rem;
  &::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #4ade80;
    margin-right: 0.4rem;
    animation: ${dotPulse} 1.4s ease-in-out infinite;
    vertical-align: middle;
  }
`;

const EmptyState = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(74, 222, 128, 0.15);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 52px;
  height: 52px;
  margin: 0 auto 1.25rem;
  color: rgba(74, 222, 128, 0.35);
  svg {
    width: 100%;
    height: 100%;
  }
`;

const EmptyTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: #f0fdf4;
  margin: 0 0 0.6rem;
  font-family: var(--font-primary, sans-serif);
`;

const EmptyBody = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: rgba(240, 253, 244, 0.55);
  max-width: 420px;
  margin: 0 auto 1.5rem;
  font-family: var(--font-secondary, sans-serif);
`;

const PhoneEmphasis = styled.strong`
  color: #4ade80;
`;

const CallNowBtn = styled.button`
  padding: 0.7rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  background: #16a34a;
  color: #f0fdf4;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--font-primary, sans-serif);
  transition: background 0.2s;
  &:hover { background: #15803d; }
`;

const HintFooter = styled.p`
  font-size: 0.775rem;
  color: rgba(240, 253, 244, 0.25);
  text-align: center;
  margin: 2.5rem 2rem 0;
  font-family: var(--font-secondary, sans-serif);
  font-style: italic;
`;
